import { get } from '@/api/client';
import { useAuth } from '@/auth/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BreakMode } from '@timefly/core';
import { useCallback, useEffect, useRef } from 'react';

/**
 * When the company is open, per weekday, for the phone's availability canvas
 * (backend/services/openingHours.js, 2026-09-20).
 *
 * `["08:00", "22:00"]` or `null` for a day it is shut. The whole object null,
 * or absent, means nothing has been set, which is not the same as closed: the
 * clients fall back to 06:00-24:00 open rather than greying everything out.
 */
export type OpeningHours = Partial<
  Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', [string, string] | null>
>;

/** The tenant row as GET /api/v1/tenant returns it. Only the fields web/ reads. */
export interface Tenant {
  id: number;
  name: string;
  locale?: string | null;
  lunch_included?: boolean;
  /** auto, punched or punched_min (plan 0032); lunch_included is kept equal to auto for the old clients */
  break_mode?: BreakMode;
  lunch_length_minutes?: number | null;
  shift_length_hours?: number | null;
  /** the company's full-time week, at most 40; null falls back to 37.5 (plan 0027 B) */
  agreed_week_hours?: number | string | null;
  /** unused timebank hours: 'pay' on avspasering_payout_day ('MM-DD'), or 'carry' (plan 0027 G2) */
  avspasering_unused?: 'pay' | 'carry' | null;
  avspasering_payout_day?: string | null;
  /** the payroll code paid-out timebank hours go out under */
  avspasering_payout_code?: string | null;
  /** lønnsart for ordinary hours; null reads as 100 (2026-09-28) */
  regular_pay_code?: string | null;
  /** lønnsart for the 1 and 17 May supplement; null leaves the line without one (#26, 2026-10-12) */
  hoytid_pay_code?: string | null;
  /** #143: an exempt person (AML §10-12) on a fixed salary gets no supplement lines either; off by default */
  exempt_salaried_no_supplements?: boolean;
  merarbeid_plain_pay?: boolean;
  /** Innstillinger → Lønn «Lønnssystem» (#110): which payroll system's file Timer til lønn makes; tripletex by default */
  payroll_system?: 'tripletex' | 'poweroffice' | 'visma';
  /** plan 0027 batch C: the organisation number, what the Enhetsregisteret says for it, the tariff flag and the 52-week period start */
  org_number?: string | null;
  brreg_name?: string | null;
  nace_code?: string | null;
  nace_text?: string | null;
  tariff_bound?: boolean;
  /** issue #70: availability an employee enters waits for a manager's approval */
  availability_requires_approval?: boolean;
  /** Innstillinger → Prosjekter (#115): a shift should carry a project; the company's word for one (null is Prosjekt) */
  projects_required?: boolean;
  projects_label?: 'Prosjekt' | 'Oppdrag' | 'Objekt' | null;
  /** #132: the share of a project's budgeted hours where it warns; null is 80 */
  budget_warn_percent?: number | null;
  overtime_period_start?: string | null;
  /** Innstillinger → Varsler (#124, #133); null or absent is the defaults */
  push_settings?: { off?: string[]; shift_lead_minutes?: number; daily_at?: string } | null;
  /** plan 0028 slice 2: a visitor's private demo, and when it is deleted */
  is_demo?: boolean;
  demo_expires_at?: string | null;
  /** plan 0028 slice 3: the free trial's end, and where the administrator is in the first-run setup */
  trial_ends_at?: string | null;
  setup_step?: 'settings' | 'company' | 'employees' | 'rules' | 'hugin' | 'review' | null;
  /** whether this company has ever published a shift; the first-plan hint reads it (2026-09-29) */
  has_published_shift?: boolean;
  /** plan 0028 slice 4: Stripe's status copied onto the company, and what the trial's end allows */
  subscription_status?: string | null;
  billing?: { enabled: boolean; access: 'full' | 'read' | 'locked' };
  is_gps_enabled?: boolean;
  gps_radius_meters?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  /** how vacation days are counted when charged (2026-09-18) */
  vacation_day_basis?: 'virkedager' | 'arbeidsdager';
  /** when the company is open, per weekday; null or absent means not set */
  opening_hours?: OpeningHours | null;
  workspace?: Workspace;
  /** the caller's own role here and what it grants, from the same answer (custom roles, 2026-09-16) */
  me?: Me;
}
/**
 * GET /tenant `me`: the key this membership holds in this company and the permissions that
 * key is defined to carry here. The server decides; this is so a screen can offer what the
 * server will accept instead of guessing from a role name. It comes with the company, so
 * switching company replaces it, and an edit to the role invalidates the query.
 */
export interface Me {
  /** the key the membership holds: an identifier, not authority */
  role: string;
  /** the company's protected administrator, verified by the server against its own role row */
  is_admin: boolean;
  /** when this member was shown the welcome overview here; null before, absent from an older server (#130) */
  welcomed_at?: string | null;
  permissions: string[];
}
/** GET /tenant `workspace` (contract 2026-09-13): what is enabled here, what this release ships. Time is always on. */
export interface Workspace {
  schema_version: number;
  revision: number;
  enabled: { time: boolean; scheduling: boolean; projects: boolean };
  available: { time: boolean; scheduling: boolean; projects: boolean };
  /** who may look, rather than what exists: modules are `enabled` (TF-SIMPLE-ROLES, 2026-09-16) */
  visibility: { coworker_shifts: boolean };
  /** the first working day, chosen in Kom i gang (#294); absent from an older server */
  start_date?: string | null;
}
export type Mode = 'rota' | 'timer';

/** The compatibility default the server answers for a company with no row. */
export const DEFAULT_WORKSPACE: Workspace = {
  schema_version: 1,
  revision: 0,
  enabled: { time: true, scheduling: true, projects: false },
  visibility: { coworker_shifts: true },
  available: { time: true, scheduling: true, projects: false },
};

/**
 * The screens still ask for a `mode`; since 2026-09-13 it is derived from the
 * server's workspace, not from this browser: scheduling on is the rota home,
 * scheduling off is the timesheet home. The old per-browser override is read
 * only to offer the admin a one-time "save this for the company" (Innstillinger).
 */
export const modeOf = (w: Workspace | undefined): Mode =>
  (w ?? DEFAULT_WORKSPACE).enabled.scheduling ? 'rota' : 'timer';
export function legacyModeOverride(tenantId: number | undefined): Mode | null {
  try {
    const v = localStorage.getItem(`timefly.mode.${tenantId}`);
    return v === 'timer' || v === 'rota' ? v : null;
  } catch {
    return null;
  }
}
export function clearLegacyModeOverride(tenantId: number | undefined) {
  try {
    localStorage.removeItem(`timefly.mode.${tenantId}`);
  } catch {
    /* ignore */
  }
}

/**
 * The verified company of the current context. `tenant` is undefined until
 * GET /tenant has answered under this generation; the shell shows nothing
 * company-bound before that. Switching lives in shell/switch.ts.
 */
export function useTenant() {
  const token = useAuth((s) => s.token);
  // The company's answer carries what this person may do, so it is the one query that is
  // re-read when the window comes back: a permission taken away upstairs is noticed on the
  // next focus, not at the next login. usePolicyWatch below acts on what it finds.
  const tenantQ = useQuery({
    queryKey: ['tenant'],
    queryFn: () => get<Tenant>('/tenant'),
    enabled: !!token,
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    refetchOnMount: true,
  });
  const tenantsQ = useQuery({
    queryKey: ['my-tenants'],
    // answers { active_tenant, tenants } (tenantController getMyTenants); the old app's fetcher unwrapped it too
    queryFn: async () => {
      const r = await get<{
        active_tenant: number;
        tenants: Tenant[];
        memberships?: { tenant_id: number; role: string }[];
      }>('/tenants/my-tenants');
      return { tenants: r.tenants ?? [], memberships: r.memberships ?? [] };
    },
    enabled: !!token,
    staleTime: 60_000,
  });
  const tenant = tenantQ.data;
  const workspace = tenant?.workspace ?? DEFAULT_WORKSPACE;
  return {
    tenant,
    workspace,
    /** Nothing until the company is verified: no permission is assumed before the server says so. */
    me: tenant?.me,
    tenants: tenantsQ.data?.tenants ?? [],
    /** the caller's role in each company, from the same answer (TF-PRESERVATION-FINAL) */
    memberships: tenantsQ.data?.memberships ?? [],
    mode: modeOf(tenant ? tenant.workspace : undefined),
    /** pending until the company is verified for this context, error when it could not be loaded */
    tenantStatus: tenantQ.status,
    /** the refusal or failure behind an error status; the boot path reads its HTTP status */
    tenantError: tenantQ.error,
    refetchTenant: () => tenantQ.refetch(),
  };
}

/**
 * Private data cached under an authority that has since been reduced.
 *
 * What is compared is the **authority**, not the role name: the permissions *and* whether
 * the server still calls this person the company's administrator. A reassignment from a
 * privileged role to Ansatt changes the key and is the commonest revocation there is, so
 * comparing keys and giving up when they differ, which is what this did first, skipped
 * exactly the case it exists for. Losing `is_admin` while the permission list happens to
 * stay the same is the same mistake from the other side (Codex review, 2026-09-16).
 *
 * Isolation is by company. A different company is a different context with its own
 * generation, and its answer says nothing about what was cached under the previous one.
 *
 * When authority narrows, every cached answer is **cancelled and then removed**, not
 * invalidated. Invalidation leaves the rows in the cache and React Query renders them while
 * it refetches, so a screen would paint a colleague's hours once more after the permission
 * to see them was taken away; and a fetch already in flight under the old authority would
 * write its answer back into the cache after the removal, which the cancellation prevents.
 *
 * Only a reduction does this. Gaining a permission needs no clearing.
 */
type Authority = { tenantId: number | undefined; admin: boolean; perms: string[] };

const narrower = (before: Authority, now: Authority) =>
  (before.admin && !now.admin) || before.perms.some((p) => !now.perms.includes(p));

export function usePolicyWatch() {
  const qc = useQueryClient();
  const { tenant, me, tenantStatus, tenantError } = useTenant();
  const seen = useRef<Authority | null>(null);

  /** Everything private: not the company's own answer, which is what reported the change. */
  const dropPrivate = useCallback(() => {
    const isPrivate = (q: { queryKey: readonly unknown[] }) =>
      q.queryKey[0] !== 'tenant' && q.queryKey[0] !== 'my-tenants';
    // cancel first: a fetch already in flight under the old authority would otherwise write
    // its answer back into the cache after the removal
    qc.cancelQueries({ predicate: isPrivate });
    qc.removeQueries({ predicate: isPrivate });
  }, [qc]);

  useEffect(() => {
    if (!me) return;
    const now: Authority = {
      tenantId: tenant?.id,
      admin: me.is_admin === true,
      perms: [...me.permissions].sort(),
    };
    const before = seen.current;
    seen.current = now;
    if (!before || before.tenantId !== now.tenantId) return; // first answer, or another company
    if (narrower(before, now)) dropPrivate();
  }, [me, tenant?.id, dropPrivate]);

  /**
   * An authoritative refusal on the re-read: the server says this person may not have this
   * company any more. Whatever is on screen was fetched under an authority that is gone, so
   * it goes too. A network failure is deliberately not this case: nothing was refused, the
   * last answer stands, and the server still refuses anything the screen tries to do.
   */
  useEffect(() => {
    if (tenantStatus !== 'error') return;
    const status = (tenantError as { status?: number } | null)?.status;
    if (status === 401 || status === 403) {
      seen.current = null;
      dropPrivate();
    }
  }, [tenantStatus, tenantError, dropPrivate]);
}
